import { jsx as _jsx } from "react/jsx-runtime";
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loading } from 'Shared/components';
import { ELevelResult, } from 'Shared/types';
import { DEFAULT_LOADING_LABEL } from 'Common/consts';
import NoLevelResultsData from 'Common/components/NoLevelResultsData/NoLevelResultsData';
import { isDatasetWithAncestry } from 'Common/utils';
import { ELoadingStatus } from 'API/types';
import useRouter from 'Router/index';
import { getSelectedDataset, getSelectedDatasetProviderId, useSelectedDatasetName, } from 'Datasets/store';
import { fetchGPAData, fetchPLRData } from 'PhenotypeView/store/actions';
import { getPhenotype, getLoadingMessage, getErrorType, getTraitsType, getLoadingStatus, getSelectors, } from 'PhenotypeView/store/selectors';
import { getErrorMessage } from 'PhenotypeView/utils';
import { useValidatedPhenotypeId } from 'PhenotypeView/hooks';
import TabContent from './TabContent';
const TabContentContainer = ({ levelResult, columnsData, getColumns, useBinaryPlotTooltip, useContinuousPlotTooltip, getBinaryPlotDataPointShape, getContinuousPlotDataPointShape, }) => {
    const dispatch = useDispatch();
    const routeParamsRef = useRef(null);
    const { getParams } = useRouter();
    const { datasetVersionId } = getParams();
    const phenotypeId = useValidatedPhenotypeId();
    const selectedDatasetName = useSelectedDatasetName();
    const hasAncestryData = isDatasetWithAncestry(selectedDatasetName);
    const traitsType = useSelector(getTraitsType);
    const phenotype = useSelector(getPhenotype);
    const loadingMessage = useSelector(getLoadingMessage);
    const loadingStatus = useSelector(getLoadingStatus);
    const providerId = useSelector(getSelectedDatasetProviderId);
    const dataset = useSelector(getSelectedDataset);
    const errorType = useSelector(getErrorType);
    const { getAncestries } = getSelectors(levelResult);
    const ancestries = useSelector(getAncestries);
    const hasMultipleAncestries = hasAncestryData && ancestries.length > 1;
    /**
     * Recreate routeParams to avoid redundant updates
     */
    const routeParams = useMemo(() => ({
        phenotypeId,
        datasetVersionId,
        levelResult,
    }), [phenotypeId, datasetVersionId, levelResult]);
    useLayoutEffect(() => {
        routeParamsRef.current = routeParams;
        const fetchAction = dispatch(routeParams.levelResult === ELevelResult.Phenotype
            ? fetchPLRData(routeParams)
            : fetchGPAData(routeParams));
        return () => {
            fetchAction.abort();
        };
    }, [dispatch, routeParams]);
    if (routeParamsRef.current !== routeParams) {
        return null;
    }
    if (loadingStatus === ELoadingStatus.Loading) {
        // Show fullscreen loading message until `dataExists` flag would be set
        return (_jsx(Loading, { message: loadingMessage, fullScreen: false, label: DEFAULT_LOADING_LABEL }, void 0));
    }
    if (errorType !== null) {
        return (_jsx(NoLevelResultsData, { message: getErrorMessage(levelResult) }, void 0));
    }
    if (!providerId) {
        return null;
    }
    return (_jsx(TabContent, { levelResult: levelResult, datasetName: dataset === null || dataset === void 0 ? void 0 : dataset.name, datasetId: dataset === null || dataset === void 0 ? void 0 : dataset.id, phenotypeName: phenotype.name, traitsType: traitsType, providerId: providerId, columnsData: columnsData, getColumns: getColumns, useBinaryPlotTooltip: useBinaryPlotTooltip, useContinuousPlotTooltip: useContinuousPlotTooltip, getBinaryPlotDataPointShape: getBinaryPlotDataPointShape, getContinuousPlotDataPointShape: getContinuousPlotDataPointShape, description: dataset === null || dataset === void 0 ? void 0 : dataset.description, hasAncestryData: hasAncestryData, hasMultipleAncestries: hasMultipleAncestries }, void 0));
};
export default TabContentContainer;
